import { ReadonlyHeaders } from 'next/dist/server/web/spec-extension/adapters/headers'
import { floorNumber } from '@node-space/utils'
import { captureMessage, withScope } from '@sentry/nextjs'
import { validate as isUuid } from 'uuid'
import { Currency, isBvnkCurrency, isLayer1Currency } from '@/types/currency'
import { Locale } from '@/types/locale'
import { LogLevelSeverity } from '@/types/log'
import { PaymentSummary } from '@/types/payment'
import { ScreenSize } from '@/types/screenSize'

/**
 * isValidUuid - Validates where a string is a valid UUID V4 (which we use in DB)
 * @param {string} uuid - the uuid to validate against
 * @returns boolean
 */
export const isValidUuid = (uuid: string): boolean => isUuid(uuid)

/**
 * getCurrencyPrecision - Returns the precision of the currency
 * @param {string} currencyName - the name of the currency
 * @param {Currency[]} currencies - array of available currencies
 * @returns number
 */
export const getCurrencyPrecision = (currencyName: string, currencies: Currency[]): number => {
  if (!currencies?.length) {
    return 0
  }
  const currentCurrency = currencies.find(curr => curr.code === currencyName)
  if (!currentCurrency) {
    return 4 // for fiat or unknown currency
  }
  return currentCurrency.displayPrecision
}

/**
 * secondToHms - Returns an object of hours, minutes and seconds
 * @param {number} d - the seconds value to convert
 * @returns object
 */
export const secondsToHms = (d: number) => {
  const h = Math.floor(d / 3600)
  const m = Math.floor((d % 3600) / 60)
  const s = Math.floor((d % 3600) % 60)
  return { hours: h, minutes: m, seconds: s }
}

/**
 * addPadding - Returns a string and adds padding if value is less than 10
 * @param {number} time - the time value
 * @returns string
 */
export const addPadding = ({ time }: { time: number }) => {
  return time < 10 ? `0${time}` : time
}

/**
 * secondsToDaysAndHours - Returns an object of days, hours and minutes
 * @param {number} s - the seconds value to convert
 * @returns object
 */
export const secondsToDaysAndHours = (s: number) => {
  const days = Math.floor(s / 86400)
  const hours = Math.floor((s % 86400) / 3600)
  const minutes = Math.floor((s % 3600) / 60)
  return { days, hours, minutes }
}

/**
 * Promise timer to pause and help async functions delay
 * @returns Promise<void>
 */
export const sleep = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms))

/**
 * getEventName - Returns string of the Amplitude event name based on pathName
 * @param {number} pathName - the pathName
 * @returns string
 */
export const getEventName = (pathName: string) => {
  if (pathName === 'seen') {
    return 'Payment In Progress Page'
  } else if (pathName === 'done') {
    return 'Payment Done Page'
  }
}

/**
 * isScreenSize - Evaluates the screen size
 * @param {ScreenSize} screenSize -  the screen size
 * @returns boolean
 */
export const isScreenSize = (screenSize: ScreenSize) => {
  type Breakpoints = {
    [key: string]: string
  }

  const breakpoints: Breakpoints = {
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
  }

  const mediaQuery = `(min-width: ${breakpoints[screenSize]})`
  if (typeof window !== 'undefined') {
    return window.matchMedia(mediaQuery).matches
  }
}

/**
 * isMobileDevice - Evaluates if the device is mobile
 * @param {headersList} ReadonlyHeaders -  HTTP incoming request headers
 * @returns boolean
 */
export const isMobileDevice = (headersList: ReadonlyHeaders): boolean => {
  const userAgent = headersList.get('user-agent')
  const isDesktopViewer = headersList.get('cloudfront-is-desktop-viewer')
  const isMobileViewer = headersList.get('cloudfront-is-mobile-viewer')
  const isTabletViewer = headersList.get('cloudfront-is-tablet-viewer')

  const mobileRegex = /Android|iPhone|BlackBerry|IEMobile/i
  const isDesktop = parseBoolean(isDesktopViewer)
  const isMobile = parseBoolean(isMobileViewer)
  const isTablet = parseBoolean(isTabletViewer)

  if (!userAgent) {
    return false
  }

  if (isDesktop) {
    return false
  }

  if (isMobile && !isTablet) {
    return true
  }

  return mobileRegex.test(userAgent)
}

/**
 * parseBoolean - Evaluates and converts string value to boolean value
 * @param {string | null} value - string value to be converted
 * @returns {boolean}
 */
const parseBoolean = (value: string | null): boolean => {
  return value?.toLowerCase() === 'true'
}

/**
 * calculateFutureDate - Calculates the future date by adding the specified time values to the current date and time.
 * @param {TimeValues} timeValues - An object containing time values in days, hours, minutes, and seconds.
 * @returns {Date} - The future date as Date object.
 */
interface TimeValues {
  days?: number
  hours?: number
  minutes?: number
  seconds?: number
}

export const calculateFutureDate = (timeValues: TimeValues): Date => {
  const { days, hours, minutes, seconds } = timeValues
  const daysInMs = days ? days * 24 * 60 * 60 * 1000 : 0
  const hoursInMs = hours ? hours * 60 * 60 * 1000 : 0
  const minutesInMs = minutes ? minutes * 60 * 1000 : 0
  const secondsInMs = seconds ? seconds * 1000 : 0

  const totalMilliseconds = daysInMs + hoursInMs + minutesInMs + secondsInMs

  const currentTimestamp = new Date().getTime()

  const futureTimestamp = currentTimestamp + totalMilliseconds

  const futureDate = new Date(futureTimestamp)

  return futureDate
}

/**
 * getFullLocale - Evaluates and converts simple locale to full locale (en to US-en)
 * @param {string} locale - string value to be converted
 * @returns {string}
 */
export const getFullLocale = (locale: string): string => {
  switch (locale) {
    case Locale.SimplifiedChinese:
      return 'zh-CN'
    case Locale.Turkish:
      return 'tr-TR'
    case Locale.Spanish:
      return 'es-ES'
    case Locale.French:
      return 'fr-FR'
    case Locale.Japanese:
      return 'ja-JP'
    case Locale.SouthKorean:
      return 'ko-KR'
    case Locale.Portuguese:
      return 'pt-BR'
    default:
      return 'en-US'
  }
}

/**
 * logSentryError captures a message and exception in Sentry.
 * @param {String} context - identifies the main context of the error.
 * @param {error} error - the errror message
 * @param {object} properties - any extra info you want to pass down with the error
 * @param {logLevel} logLevel - the level of logging
 * @returns void
 */

export const logSentryError = (
  context: string,
  error: unknown,
  properties: object = {},
  logLevel: LogLevelSeverity = 'error'
) => {
  withScope(scope => {
    const errorData =
      typeof error === 'object' && error && 'data' in error
        ? (error as unknown as { data: { errorList: string[] } }).data
        : undefined
    scope.setExtra('Error List', errorData?.errorList ?? errorData ?? error)
    scope.setExtra('Addional Info', properties)
    scope.setLevel(logLevel)
    captureMessage(context, logLevel)
  })
}

/**
 * Converts a string to a decimal value while cleaning and formatting it.
 * @param {string} value - The input string to be converted to a decimal.
 * @returns {string} - The cleaned and formatted decimal string.
 */
export const stringToDecimal = (value: string) => {
  return value
    .replace(/[^0-9.]/g, '') // Remove non-numeric and non-decimal point characters
    .replace(/(\.\d*)\..*/, '$1') // Allow only one decimal point
    .replace(/^0+(\d+(\.\d{1,40})?).*/, '$1$2') // Remove leading zeros
}

/**
 * getLocale - Evaluates and converts the full locale to simple locale (en-US to en)
 * @param {string} locale - string value to be converted
 * @returns {string}
 */
export const getLocale = (locale: string): string => {
  switch (locale) {
    case 'zh-CN':
      return Locale.SimplifiedChinese
    case 'es':
    case 'es-ES':
      return Locale.Spanish
    case 'fr':
    case 'fr-FR':
      return Locale.French
    case 'tr':
    case 'tr-TR':
    case 'tr-CY':
      return Locale.Turkish
    default:
      return Locale.English
  }
}

/**
 * isLocalStorageAvailable -Checks if the localStorage is available and usable in the browser.
 * @returns {boolean} - Returns true if localStorage is available and usable, false otherwise.
 */
export const isLocalStorageAvailable = (): boolean => {
  try {
    const testKey = 'test'
    localStorage.setItem(testKey, testKey)
    localStorage.removeItem(testKey)
    return true
  } catch (e) {
    return false
  }
}

/**
 * detectCountry - detects the end-users country and returns the code and name
 * @param {headersList} ReadonlyHeaders -  HTTP incoming request headers
 * @returns {string} - Returns the country code of the end-user
 */
export const detectCountry = (headersList: ReadonlyHeaders): string => {
  const code = headersList.get('cloudfront-viewer-country') || ''

  return code
}

/**
 * Generates an HMAC SHA-256 signature of the payload using the provided secret.
 *
 * @param payload - The payload to sign. If not a string, it will be JSON-stringified.
 * @param secret - The secret key used for signing.
 * @returns A Promise resolving to the base64-encoded signature.
 */
export async function generateHmacSignature(payload: unknown, secret: string): Promise<string> {
  const encoder = new TextEncoder()

  // Encode the secret key.
  const secretKeyData = encoder.encode(secret)

  // Import the secret key for HMAC-SHA-256.
  const key = await crypto.subtle.importKey(
    'raw',
    secretKeyData,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  )

  // Convert payload to string (if necessary) and encode it.
  const payloadString = typeof payload === 'string' ? payload : JSON.stringify(payload)
  const data = encoder.encode(payloadString)

  // Sign the data.
  const signatureBuffer = await crypto.subtle.sign('HMAC', key, data)

  // Convert the signature to base64.
  return arrayBufferToBase64(signatureBuffer)
}

/**
 * Converts an ArrayBuffer to a Base64 encoded string.
 *
 * @param buffer - The ArrayBuffer to convert.
 * @returns The Base64 encoded string representation of the ArrayBuffer.
 */
const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return btoa(binary)
}

/**
 * getCounterRate - Returns the counter rate of the payment summary
 * @param {string} counterCurrency - the counter currency
 * @param {Currency[]} currencies - array of available currencies
 * @param {PaymentSummary} paymentSummary - the payment summary
 * @returns {number} - the counter rate
 */
export const getCounterRate = (
  counterCurrency: string,
  currencies: Currency[],
  paymentSummary?: PaymentSummary
): number => {
  const isPegged = paymentSummary?.pegged ?? false
  const precision = getCurrencyPrecision(counterCurrency, currencies)
  const rate = isPegged
    ? (paymentSummary?.pegStablecoinDetails?.exchangeRate?.rate ?? 0)
    : (paymentSummary?.displayRate?.rate ?? 0)

  return floorNumber(rate, precision)
}

/**
 * getCurrencyProtocol - Returns the currency protocol
 * @param {Currency} currency - the currency
 * @param {PaymentSummary} paymentSummary - the payment summary
 * @returns {string} - the currency protocol
 */
export const getCurrencyProtocol = (currency: Currency, paymentSummary: PaymentSummary) => {
  if (isBvnkCurrency(currency)) {
    return currency.enabledProtocols?.find(
      protocol => protocol.code === paymentSummary?.address?.protocol
    )
  }

  // For Layer1 currencies, create a protocol-like object from the network
  if (isLayer1Currency(currency)) {
    return {
      code: paymentSummary?.address?.protocol || '',
      network: currency.network,
    }
  }
}
